import { DocumentPreviewSkeleton } from "@/components/DocumentPreviewSkeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { type SpreadsheetViewerViewProps } from "./types";

// Matches the flat, underlined look of the top-level view tabs (MedicalChronology/ViewTabs.tsx),
// with the active indicator on the top edge since this strip sits below its content, not above it.
const SHEET_TAB_CLASS =
  "relative h-9 shrink-0 rounded-none border-t-2 border-transparent bg-transparent px-3 text-sm font-bold text-neutral-500 transition-all hover:text-neutral-700 data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none";

export function SpreadsheetViewerView({
  fileName,
  isLoading,
  hasLoadError,
  sheetNames,
  activeSheetIndex,
  rows,
  columnCount,
  onSheetChange,
}: SpreadsheetViewerViewProps) {
  if (isLoading) {
    return <DocumentPreviewSkeleton variant="content" />;
  }

  if (hasLoadError) {
    return (
      <div className="flex h-full items-center justify-center p-4 text-center text-sm text-muted-foreground">
        Couldn&apos;t preview this file
      </div>
    );
  }

  const [headerRow, ...bodyRows] = rows;
  const columnIndexes = Array.from({ length: columnCount }, (_, index) => index);

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="min-h-0 flex-1 overflow-auto p-4">
        {headerRow ? (
          <table aria-label={fileName} className="border-collapse text-xs text-foreground">
            <thead>
              <tr className="bg-muted">
                {columnIndexes.map((colIndex) => (
                  <th
                    key={colIndex}
                    className="whitespace-nowrap border border-border-strong px-2 py-1 text-left font-semibold"
                  >
                    {headerRow[colIndex] ?? ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columnIndexes.map((colIndex) => (
                    <td key={colIndex} className="whitespace-nowrap border border-border px-2 py-1">
                      {row[colIndex] ?? ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            This sheet is empty
          </div>
        )}
      </div>
      {sheetNames.length > 1 && (
        <Tabs
          value={String(activeSheetIndex)}
          onValueChange={(value) => onSheetChange(Number(value))}
          className="shrink-0 border-t border-border"
        >
          <TabsList className="h-auto w-full justify-start gap-0 overflow-x-auto rounded-none bg-transparent p-0">
            {sheetNames.map((sheetName, sheetIndex) => (
              <TabsTrigger key={sheetName} value={String(sheetIndex)} className={SHEET_TAB_CLASS}>
                {sheetName}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
    </div>
  );
}
